import { type ReactNode, useRef, useState } from 'react'

import { CheckIcon } from '@/components/icons'
import { cx } from '@/lib/cx'
import { useDismiss } from '@/lib/use-dismiss'

/**
 * An icon button in the header that drops a small panel; the panel gets
 * `close` for its links. `panel` sets its width and, if needed, its offset.
 */
export function HeaderMenu({
  label,
  icon,
  panel = 'right-0 w-48',
  children,
}: {
  label: string
  icon: ReactNode
  panel?: string
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = () => setOpen(false)
  useDismiss(open, ref, close)

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="text-accent-ink hover:bg-accent-soft flex min-h-tap min-w-tap items-center justify-center rounded-lg"
      >
        {icon}
      </button>
      {open && (
        <div className={cx('bg-surface border-border absolute top-full z-40 mt-1 rounded-xl border p-1 shadow-lg', panel)}>
          {children(close)}
        </div>
      )}
    </div>
  )
}

export function MenuOption({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cx(
        'hover:bg-accent-soft flex min-h-tap w-full items-center justify-between gap-2 rounded-lg px-3 text-left',
        selected && 'text-accent-ink',
      )}
    >
      {children}
      {selected && <CheckIcon className="size-4" />}
    </button>
  )
}
